/* eslint-disable no-underscore-dangle */
import React, { PureComponent } from 'react';
import config from 'universal-config';
import PropTypes from 'prop-types';
import { NullErrorBoundary } from 'components/ErrorBoundary/NullErrorBoundary';
import { VenueItem } from 'features/Search/components/VenueItem';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import { isEmpty } from 'helpers/verify';
import { deepExtract } from 'helpers/deep';
import { VenueMapMarker } from './VenueMapMarker';
import { importMapboxDependencies } from './SearchMap.helpers';
import { SearchNoResultsContainer } from '../SearchNoResultsContainer';
import { ResultTypeSelectorContainer } from '../ResultTypeSelectorContainer';
import css from '../Search.scss';

const DEFAULT_ZOOM = 10;
const DEFAULT_TILE_LAYER_MAX_ZOOM = 18;
const DEFAULT_TILE_LAYER_MIN_ZOOM = 3;
export const LOAD_REQUEST_DELAY_MS = 300;

export class SearchMap extends PureComponent {
  constructor(props) {
    super(props);

    this.mapRef = React.createRef();
    this.state = {
      isMapModuleLoaded: false,
    };
  }

  componentDidMount() {
    if (__CLIENT__) {
      importMapboxDependencies().then(({ ReactMapGL: { default: ReactMapGL, NavigationControl } }) => {
        this.ReactMapGL = ReactMapGL;
        this.NavigationControl = NavigationControl;
        this.setState({ isMapModuleLoaded: true });
      });
    }
  }

  // After new data has been loaded into the map we reset the map state (ex. removing the popup)
  componentDidUpdate(prevProps) {
    const { loading, updateViewport, viewport } = this.props;
    const hasLoadedNewData = prevProps.loading && !loading;
    // We update the viewport if new data has come from the api or if the zoom hasn't been set
    const shouldUpdateViewport = hasLoadedNewData || !viewport.zoom;
    // We update the viewport the map after the map api has been initialized
    if (shouldUpdateViewport && this.isMapRefInitialized()) {
      const width = deepExtract('mapRef.current._width', this);
      const height = deepExtract('mapRef.current._height', this);
      updateViewport(width, height);
    }
  }

  componentWillUnmount() {
    const { setMapLoaded } = this.props;

    setMapLoaded(false);
  }

  handleMapClick = event => {
    const { onMapClick } = this.props;
    // only register the map click if the map itself (overlays) is clicked
    if (event.target.classList.contains('overlays')) {
      onMapClick();
    }
  };

  handleMapLoad = event => {
    const { setMapLoaded } = this.props;
    const { target } = event;

    // This will trigger an update to fill map bounds with results, in order to get correct map dimensions, we need to ensure styles have loaded
    // Setting timeout to ensure enough time has passed for styles to load
    setTimeout(() => {
      const width = deepExtract('transform.width', target);
      const height = deepExtract('transform.height', target);
      setMapLoaded(true, { width, height });
    }, LOAD_REQUEST_DELAY_MS);
  };

  handleMapSearch = () => {
    const { onMapSearch } = this.props;
    const bounds = this.mapRef.current.getMap().getBounds();
    onMapSearch(this.boundsToString(bounds));
  };

  isMapRefInitialized = () => {
    const width = deepExtract('mapRef.current._width', this);
    const height = deepExtract('mapRef.current._height', this);
    return !!(width && height);
  };

  boundsToString(bounds) {
    const northEast = bounds._ne;
    const southWest = bounds._sw;
    return [southWest.lng, southWest.lat, northEast.lng, northEast.lat].join(',');
  }

  render() {
    const { isMapModuleLoaded } = this.state;
    const { ReactMapGL, NavigationControl } = this;
    const {
      isLoggedIn,
      isSearchable,
      venues,
      onMarkerSelected,
      popup,
      highlightedVenueId,
      latitude,
      longitude,
      viewport,
      loading,
      isMobileMapView,
      onMapMove,
      closeOpenPanel,
    } = this.props;
    const hasMapData = latitude && longitude;
    const shouldRenderMap = !!(hasMapData || !this.isMapRefInitialized());
    const isActivePopup = !isEmpty(popup);
    const { apiKey, mapStyle } = config().mapbox;

    return (
      <NullErrorBoundary>
        <div
          className={cx('search-result-map', css.Search__map, {
            'search-result-map--searching': loading,
          })}>
          {isMapModuleLoaded && shouldRenderMap && (
            <ReactMapGL
              latitude={latitude}
              longitude={longitude}
              {...viewport}
              maxZoom={DEFAULT_TILE_LAYER_MAX_ZOOM}
              minZoom={DEFAULT_TILE_LAYER_MIN_ZOOM}
              zoom={(viewport && viewport.zoom) || DEFAULT_ZOOM}
              width="100%"
              height="100%"
              touchRotate
              mapStyle={`mapbox://styles/classpass/${mapStyle}`}
              mapboxApiAccessToken={apiKey}
              onViewportChange={onMapMove}
              onTouchStart={closeOpenPanel}
              ref={this.mapRef}
              onClick={this.handleMapClick}
              onLoad={this.handleMapLoad}>
              <div className="search-result-map__navigation-control">
                <NavigationControl onViewportChange={onMapMove} showCompass={false} />
              </div>
              <div
                className={cx('search-result-map__button', {
                  'search-result-map__button--visible': isSearchable || loading,
                  'search-result-map__button--searching': loading,
                })}
                onClick={this.handleMapSearch}
                data-qa="SearchMap.redo-search">
                {loading ? (
                  <LoadingDots />
                ) : (
                  <FormattedMessage defaultMessage="Redo search in this area" id="search.map.redo-search" />
                )}
              </div>
              {venues && venues.length ? (
                <VenueMapMarker
                  venues={venues}
                  isActivePopup={isActivePopup}
                  onMarkerSelected={onMarkerSelected}
                  highlightedVenueId={highlightedVenueId}
                  popupId={popup && popup.id}
                />
              ) : (
                isMobileMapView && (
                  <div className="search-result-map__popup search-result-map__no-results">
                    <div className="search-result-map__popup__content">
                      <SearchNoResultsContainer />
                    </div>
                  </div>
                )
              )}
              {isMobileMapView && isLoggedIn && (
                <div className="search-results-map__result-tabs">
                  <ResultTypeSelectorContainer hideCount />
                </div>
              )}
              {isActivePopup && (
                <div className="search-result-map__popup">
                  <VenueItem
                    className="search-result-map__popup__content"
                    excludeDescription
                    excludeFeaturedReview
                    {...popup}
                  />
                </div>
              )}
            </ReactMapGL>
          )}
        </div>
      </NullErrorBoundary>
    );
  }
}

SearchMap.propTypes = {
  isLoggedIn: PropTypes.bool,
  loading: PropTypes.bool,
  location: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  venues: PropTypes.arrayOf(PropTypes.shape({})),
  highlightedVenueId: PropTypes.number,
  isMapSearch: PropTypes.bool,
  updateViewport: PropTypes.func.isRequired,
  isMobileMapView: PropTypes.bool.isRequired,
  closeOpenPanel: PropTypes.func.isRequired,
  setMapLoaded: PropTypes.func.isRequired,

  // Map store
  popup: PropTypes.shape({
    name: PropTypes.string,
    alias: PropTypes.string,
    activities: PropTypes.string,
    description: PropTypes.string,
    location: PropTypes.string,
    rating: PropTypes.number,
    ratingCount: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  isSearchable: PropTypes.bool,
  viewport: PropTypes.object,
  latitude: PropTypes.number,
  longitude: PropTypes.number,

  // Map actions
  onMapClick: PropTypes.func.isRequired,
  onMapMove: PropTypes.func.isRequired,
  onMapSearch: PropTypes.func.isRequired,
  onMarkerSelected: PropTypes.func.isRequired,
};
